import clsx from "clsx";
import {
  Modal,
  Card,
  CardActions,
  CardContent,
  Tabs,
  Tab,
  Box,
} from "@material-ui/core";
import React from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";

import { Banner } from "@/design-system/components/Banner/Banner";

import { Button } from "@/design-system/components/Button/Button";
import { makeRequest } from "@/util";
import { useModalStyles } from "./styles";
import {
  GoogleAdsIcon,
  GoogleCampaignManager360Icon,
} from "@/components/icons/themes/logos";
import { useAllSaasDestinations } from "@/hooks/useAllSaasDestinations";
import useListAllDestinationsForEnv from "../v2/hooks/useListAllDestinationsForEnv";
import { JsonFileDropzone } from "./JsonFileDropzone";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

const BoxModalOAuthServiceAccount = ({ onClose, existingConfig }) => {
  const classesModal = useModalStyles();
  const { refetch: refetchDestinations } = useListAllDestinationsForEnv();

  const [credentialsData, setCredentialsData] = React.useState(null);

  const validateFields = [
    "type",
    "project_id",
    "private_key_id",
    "private_key",
    "client_email",
    "client_id",
    "auth_uri",
    "token_uri",
    "auth_provider_x509_cert_url",
    "client_x509_cert_url",
    "universe_domain",
  ];

  const onRunOauth = () => {
    makeRequest("save_server2server_oauth", {
      google_credentials: credentialsData,
      destination_id: existingConfig?.id,
    })
      .then(() => {
        refetchDestinations();
        onClose();
      })
      .catch((error) => {
        console.error(error);
        toast.error(
          "Something went wrong. Unable to setup Google Campaign Manager 360 API.",
        );
      });
  };

  const saveEnabled = credentialsData !== null;

  const allSaasDestinations = useAllSaasDestinations();
  const docsUrl =
    allSaasDestinations[existingConfig.type_slug]?.oauthServiceAccountHelpURL ||
    "bug: missing docs url for this integration: " + existingConfig.type_slug;

  return (
    <div>
      <CardContent className={clsx(classesModal.modalContent)}>
        Configure Freshpaint to use a Service Account when connecting to the
        destination Servers. You will need to upload a JSON file containing the
        service account credentials. The JSON file can be downloaded from the
        Google Cloud Console by a user who is able to manage service accounts.
        <br />
        <br />
        Please see the following link for more information on how to create a
        service account and download the credentials:
        <br />
        <a
          className="underline"
          style={{ color: "blue" }}
          href={`${docsUrl}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {docsUrl}
        </a>
        <br />
        <br />
        <JsonFileDropzone
          validateFields={validateFields}
          onFile={setCredentialsData}
        />
        <br />
        <Banner kind="note" type="info">
          <span>
            Freshpaint&apos;s use and transfer of information received from
            Google APIs will adhere to the{" "}
            <a
              className="underline"
              style={{ color: "blue" }}
              href="https://developers.google.com/terms/api-services-user-data-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google API Services User Data Policy
            </a>
            , including the Limited Use requirements.
          </span>
        </Banner>
      </CardContent>
      <CardActions className={clsx(classesModal.actions, "justify-between")}>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        {saveEnabled && <Button onClick={onRunOauth}>Save</Button>}
      </CardActions>
    </div>
  );
};

const BoxModalOAuthUserAccount = ({ onClose, existingConfig }) => {
  const classesModal = useModalStyles();

  const onRunOauth = () => {
    makeRequest("get_oauth_url", {
      // Note: Using type_slug only works as long as slug in destinations.tsx matches the integrationName in oauth.go. This
      // is true for google destinations, but may not be true for every destination.
      integration_name: existingConfig.type_slug,
      destination_id: existingConfig?.id,
    })
      .then((response) => {
        window.location = response.data;
      })
      .catch((error) => {
        console.error(error);
        toast.error(
          "Something went wrong. Unable to setup Google Campaign Manager 360 API.",
        );
      });
  };

  const allSaasDestinations = useAllSaasDestinations();
  const docsUrl =
    allSaasDestinations[existingConfig.type_slug]?.oauthUserAccountHelpURL ||
    "bug: missing docs url for this integration: " + existingConfig.type_slug;
  const destName =
    allSaasDestinations[existingConfig.type_slug]?.docsShortName ||
    allSaasDestinations[existingConfig.type_slug]?.name ||
    "bug: unknown integration name: " + existingConfig.type_slug;

  return (
    <div>
      <CardContent className={clsx(classesModal.modalContent)}>
        Connect Freshpaint to the Google API using your own account. Clicking
        the button below will redirect you to Google to authenticate and
        authorize Freshpaint to access your {destName} account. Freshpaint will
        only request the minimum privileges necessary to send events to Google
        when you are prompted by google to provide permissions.
        <br />
        <br />
        Please see the docs for more information on how to connect using a User
        Account:
        <br />
        <a className="underline" style={{ color: "blue" }} href="{docsUrl}">
          {docsUrl}
        </a>
        <br />
        <br />
        Freshpaints’s use and transfer to any other app of information received
        from Google APIs will adhere to&nbsp;
        <a
          className="underline"
          style={{ color: "blue" }}
          href="https://developers.google.com/terms/api-services-user-data-policy"
        >
          Google API Services User Data Policy
        </a>
        , including the Limited Use requirements.
      </CardContent>
      <CardActions
        className={clsx(classesModal.actions, "absolute right-5 bottom-5")}
      >
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        <Button onClick={onRunOauth}>
          {/*
          The icon being here is part of Google's brand guidelines. The only
          destinations this modal supports are CM360 and various Ads
          destinations, so for now we pick which icon to use based on whether
          this is CM360 or not.
          */}
          {destName === "Google CM360" && (
            <GoogleCampaignManager360Icon
              className="position mr-2 fill-white"
              height="2em"
              style={{ top: -2 }}
            />
          )}
          {destName !== "Google CM360" && (
            <GoogleAdsIcon
              className="position mr-2 fill-white"
              height="2em"
              style={{ top: -2 }}
            />
          )}
          Connect With {destName}
        </Button>
      </CardActions>
    </div>
  );
};

export function GoogleOAuthConfigModal({ open, onClose, existingConfig }) {
  const classes = useModalStyles();

  const [value, setValue] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setValue(newValue);
  };

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Card
        className={clsx(
          classes.root,
          classes.largeModal,
          "h-[620px] w-[720px]",
        )}
      >
        <Tabs value={value} onChange={handleTabChange}>
          <Tab label="User Account" {...a11yProps(0)} />
          <Tab label="Service Account" {...a11yProps(1)} />
        </Tabs>
        <CustomTabPanel value={value} index={0}>
          <BoxModalOAuthUserAccount
            onClose={onClose}
            existingConfig={existingConfig}
          ></BoxModalOAuthUserAccount>
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <BoxModalOAuthServiceAccount
            onClose={onClose}
            existingConfig={existingConfig}
          ></BoxModalOAuthServiceAccount>
        </CustomTabPanel>
      </Card>
    </Modal>
  );
}
